import {
  Component, effect,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  model,
  ModelSignal,
  OnInit,
  Output,
  untracked,
} from '@angular/core';

import { flagsPosition } from '@shared/utils';

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgStyle } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { LanguageStore } from '@settings/language/data-access';
import { TelInputStore } from './data-access/tel-input.store';
import { ICountryCodesPhoneNumber } from './data-access/entities/tel-input.model';
import { SkeletonLoaderComponent } from '@shared/components/ui/skeleton-loader';
import { ScrollAreaComponent } from '@shared/components/ui/scroll-area';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ToggleElementDirective } from '@shared/directives';

type TState = 'default' | 'disabled' | 'error';

/**
 * Telephone input component with country code selection
 * @example
 * <lib-tel-input [label]="'Phone'" [placeholder]="'Enter phone number'"></lib-tel-input>
 */
@Component({
    selector: 'lib-tel-input',
    templateUrl: './tel-input.component.html',
    styleUrls: ['./tel-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: TelInputComponent,
            multi: true,
        },
    ],
    imports: [
      NgxMaskDirective,
      FormsModule,
      NgClass,
      SkeletonLoaderComponent,
      NgStyle,
      ScrollAreaComponent,
      ToggleElementDirective
    ]
})
export class TelInputComponent implements ControlValueAccessor, OnInit {
  /** Input mask for phone number formatting */
  @Input() mask = '';
  /** Placeholder text for the input field */
  @Input() placeholder = '';
  /** Label text for the input field */
  @Input() label = '';
  /** Input type (default: 'text') */
  @Input() type = 'text';
  /** Name attribute for the input */
  @Input() name = '';
  /** ID attribute for the input */
  @Input() id = '';
  /** Emits when the selected country changes */
  @Output() activeCountryObjectChange: EventEmitter<ICountryCodesPhoneNumber> =
    new EventEmitter<ICountryCodesPhoneNumber>();
  
  /**
   * The state of the input
   * @default 'default'
   * @type 'default' | 'disabled'
   */
  public readonly state: ModelSignal<TState> = model<TState>('default');

  /** Internal value of the input */
  public _value: string;
  /** Telephone input store instance */
  protected readonly telInputStore = inject(TelInputStore);
  /** Language store instance */
  private readonly languageStore = inject(LanguageStore);
  /** Loading state of the component */
  public readonly loading: InputSignal<boolean> = input<boolean>();

  public readonly fullNumber: InputSignal<string> = input<string>();

  /** Controls visibility of country dropdown list */
  public showCountryList = false;
  /** Available flag positions */
  public flagsPosition = flagsPosition;
  /** Phone number country code translations */
  public telephoneTranslation = this.telInputStore.phoneNumberCountryCode;
  /** Search expression for country filtering */
  private searchExpr = '';
  /** Timer for clearing search expression */
  private timerToClearSearch: any = null;
  /** Tracks if the input has been touched */
  private touched = false;

  /** Currently selected country object */
  public activeCountryObject: ICountryCodesPhoneNumber = {
    Region: 'BR',
    CountryDisplayName: 'Brasil',
    CountryCode: 55,
    MobileMask: '(00) 00000-0000',
    FullNumber: null
  };
  
  public readonly onFullNumber = effect(() => {
    if (this.fullNumber() && this.telephoneTranslation()) {
      untracked(() => {
        const phoneNumber = parsePhoneNumberFromString(this.fullNumber());
        const country = phoneNumber?.country;
  
        if (country) {
          const countryObj = this.telephoneTranslation().find(
            (item: ICountryCodesPhoneNumber) => item.Region === country
          );
          
          if (countryObj) {
            const newCountryObj = { 
              ...countryObj, 
              FullNumber: `+${countryObj.CountryCode}${this._value}` 
            };
            
            this.activeCountryObject = newCountryObj;
            this.activeCountryObjectChange.emit(newCountryObj);
          }
        }
      });
    }
  });

  /** Component constructor */
  constructor() {
    this.telInputStore.loadCountryCodesPhoneNumber();
    fromEvent(document, 'keyup')
      .pipe(takeUntilDestroyed())
      .subscribe((e: any) => {
        this.searchExpr += e.key;
        this.searchExprChange();
      });
    effect(() => {
      this.languageStore.getCurrentLanguage();
      this.telInputStore.loadCountryCodesPhoneNumber();
    });
  }

  /**
   * Handles changes in search expression for country filtering
   * @private
   */
  searchExprChange(): void {
    if (this.timerToClearSearch) {
      clearTimeout(this.timerToClearSearch);
    }

    this.timerToClearSearch = setTimeout(() => {
      this.searchExpr = '';
    }, 1000);

    const translation: ICountryCodesPhoneNumber = this.telephoneTranslation()?.find((item: ICountryCodesPhoneNumber) =>
      item?.CountryDisplayName?.toLowerCase()?.includes(
        this.searchExpr?.toLowerCase()
      )
    );

    if (translation && this.showCountryList) {
      const element = document.getElementById(
        translation?.CountryDisplayName
      );
      element.scrollIntoView({ behavior: 'smooth' });
      element.focus();
    }
  }

  /** Initializes the component */
  ngOnInit() {
    this.activeCountryObjectChange.emit(this.activeCountryObject);
  }

  /**
   * Marks the input as touched when it loses focus
   * @returns void
   */
  public barred(): void {
    this.onTouched?.();
    this.updateFormValidity();
  }

  /**
   * Updates the form control validity by propagating the current value
   * @private
   */
  private updateFormValidity(): void {
    this.onChange?.(this._value);
  }

  /**
   * Selects a country from the dropdown
   * @param e Event object
   * @param countryObj Selected country object
   */
  public selectCountry(e: Event, countryObj: ICountryCodesPhoneNumber): void {
    e.preventDefault();
    e.stopPropagation();
    this.activeCountryObject = countryObj;
    this.activeCountryObjectChange.emit(countryObj);
    this.showCountryList = false;
  }

  /** Callback for when the input is touched */
  public onTouched: any = () => {};
  /** Callback for when the input value changes */
  public onChange: any = () => {};

  /** Registers the current value and marks as touched */
  public registerValue(): void {
    this.markAsTouched();
    this.onChange(this.unMask(this._value));
  }

  /**
   * Removes non-digit characters from the value
   * @param val Input value
   * @private
   */
  private unMask(val: string) {
    return val ? val.replace(/\D+/g, '') : '';
  }

  /**
   * Sets the input value
   * @param ev Event containing the new value
   */
  public setValue(ev): void {
    const { value } = ev;
    this._value = value;
    this.registerValue();
  }

  /**
   * Writes a value to the input (ControlValueAccessor)
   * @param value Value to write
   */
  public writeValue(value: string): void {
    this._value = value;
    this.registerValue();
  }

  /**
   * Registers change callback (ControlValueAccessor)
   * @param fn Callback function
   */
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers touch callback (ControlValueAccessor)
   * @param fn Callback function
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /** Marks the input as touched if not already touched */
  public markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
}